import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Select,
  MenuItem,
  Typography,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  listFolderContents,
  scanFolderStructure,
  getThumbnails,
  getFullResolutionUrls,
} from "../DropboxFunctions";
import {
  InstructionsDialog,
  ResetSelectsDialog,
  ConfirmSelectsDialog,
  SelectsMadeDialog,
} from "./DialogComponents";
import SelectedImagesGrid from "./SelectedImagesGrid";
import EnhancedAccordion from "./EnhancedAccordion";
import ResizableLayout from "./ResizableLayout";
import AccordionSkeleton from "../components/AccordionSkeleton";
import { use100vh } from "react-div-100vh";
import { useTheme } from "@mui/material/styles";
import { db, functions } from "../firebaseConfig";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { format } from "date-fns";
import getJobName from "../GlobalFunctions/getJobName";
import {
  clearThumbnails,
  confirmClientSelects,
} from "./selectsHelpers";

export const sortImagesByFilename = (a, b) => {
  const fileA = a.path_display.split("/").pop();
  const fileB = b.path_display.split("/").pop();
  return fileA.localeCompare(fileB, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

// Deduplicate images by filename, keeping only the first occurrence
const deduplicateByFilename = (images) => {
  const seenFilenames = new Set();
  const deduplicated = [];

  for (const image of images) {
    const filename = image.path_display.split("/").pop();
    if (!seenFilenames.has(filename)) {
      seenFilenames.add(filename);
      deduplicated.push(image);
    }
  }

  return deduplicated;
};

const formatJobDate = (date) => {
  if (!date) return "";
  const jobDate = date.toDate ? date.toDate() : new Date(date);
  return format(jobDate, "MMMM do, yyyy");
};

const getFolderNameFromFilename = (filename) => {
  if (!filename.toLowerCase().endsWith(".jpg")) return null;
  const parts = filename.split(/(-\d{5,6}-.+?-\d+\.jpg)$/i);
  if (parts.length >= 2 && parts[0].trim()) {
    return parts[0].trim();
  }
  return null;
};

const organizeImagesIntoFolders = (images, dropboxPath) => {
  const folderStructure = {};
  const sortedImages = [...images].sort(sortImagesByFilename);

  sortedImages.forEach((image) => {
    const fileName = image.path_display.split("/").pop();
    const extractedFolder = getFolderNameFromFilename(fileName);
    if (extractedFolder) {
      if (!folderStructure[extractedFolder]) {
        folderStructure[extractedFolder] = { files: [], subfolders: {} };
      }
      folderStructure[extractedFolder].files.push(image);
      return;
    }

    const pathParts = image.path_display.split("/").filter(Boolean);
    const dropboxParts = dropboxPath.split("/").filter(Boolean);
    const relativePath = pathParts.slice(dropboxParts.length);

    if (relativePath.length <= 1) {
      if (!folderStructure.noFolder) folderStructure.noFolder = [];
      folderStructure.noFolder.push(image);
      return;
    }

    const folderName = relativePath[0];
    if (!folderStructure[folderName]) {
      folderStructure[folderName] = { files: [], subfolders: {} };
    }

    if (relativePath.length === 2) {
      folderStructure[folderName].files.push(image);
    } else {
      const subfolderName = relativePath[1];
      if (!folderStructure[folderName].subfolders[subfolderName]) {
        folderStructure[folderName].subfolders[subfolderName] = [];
      }
      folderStructure[folderName].subfolders[subfolderName].push(image);
    }
  });

  return folderStructure;
};

const getThumbnailSizeForColumns = (columns) => {
  if (columns <= 1) return "w2048h1536";
  if (columns === 2) return "w1024h768";
  return "w960h640";
};

const ImageGrid = ({
  jobID,
  embedded = false,
  onSelectedImagesChange,
  gridColumns: propGridColumns,
  resetTrigger,
  selectsMade: propSelectsMade,
}) => {
  const height = use100vh();
  const theme = useTheme();

  const [selectsMade, setSelectsMade] = useState(false);
  // Use prop selectsMade if provided (for embedded mode), otherwise use local state
  const effectiveSelectsMade =
    propSelectsMade !== undefined ? propSelectsMade : selectsMade;
  const [confirmSelectsDialogOpen, setConfirmSelectsDialogOpen] =
    useState(false);
  const [dropboxPath, setDropboxPath] = useState("");
  const [jobName, setJobName] = useState("");
  const [jobDoc, setJobDoc] = useState("");
  const [selectsAreLocked, setSelectsAreLocked] = useState(false);

  // Toggle for testing direct image loading vs thumbnails
  const [useThumbnails] = useState(true);

  // Use prop gridColumns if provided (embedded mode), otherwise use localStorage state
  const localStorageKey = "gridColumnsAcross";
  const storedColumns = parseInt(localStorage.getItem(localStorageKey), 10);
  const [gridColumns, setGridColumns] = useState(
    propGridColumns ||
      (storedColumns && storedColumns >= 1 && storedColumns <= 5
        ? storedColumns
        : 3)
  );

  const desiredThumbnailSize = useMemo(
    () => getThumbnailSizeForColumns(gridColumns),
    [gridColumns]
  );
  const desiredThumbnailSizeRef = useRef(desiredThumbnailSize);
  const lastRequestedThumbnailSizeRef = useRef(desiredThumbnailSize);

  useEffect(() => {
    desiredThumbnailSizeRef.current = desiredThumbnailSize;
  }, [desiredThumbnailSize]);

  // Update gridColumns if prop changes (for embedded mode)
  useEffect(() => {
    if (propGridColumns !== undefined) {
      setGridColumns(propGridColumns);
    }
  }, [propGridColumns]);

  // Handle external reset trigger
  useEffect(() => {
    if (resetTrigger && !effectiveSelectsMade) {
      setSelectedImages([]);
    }
  }, [resetTrigger, effectiveSelectsMade]);
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [lockedSelectedImages, setLockedSelectedImages] = useState([]);
  const [lastChangedImage, setLastChangedImage] = useState(null);
  const [priorityImagesLoaded, setPriorityImagesLoaded] = useState(false);
  const [directUrlLoadingInProgress, setDirectUrlLoadingInProgress] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const thumbnailQueue = useRef([]);
  const pendingThumbnailPaths = useRef(new Set());
  const isProcessingThumbnailQueue = useRef(false);
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Dialog state: instructions vs. selects-made dialog
  // const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const [selectsMadeDialogOpen, setSelectsMadeDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [rootFolderTitle, setRootFolderTitle] = useState("Root Folder");

  useEffect(() => {
    localStorage.setItem(localStorageKey, gridColumns);
  }, [gridColumns]);


  // Notify parent component of selected images changes
  useEffect(() => {
    if (onSelectedImagesChange) {
      onSelectedImagesChange(selectedImages);
    }
  }, [selectedImages, onSelectedImagesChange]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmSelects = async () => {
    try {
      const { result } = await confirmClientSelects({
        db,
        functions,
        jobID,
        selectedImages,
        dropboxPath,
        jobDoc,
        jobName,
      });

      if (result.data?.success) {
        // console.log("Copy process started");
        setSelectsMade(true);
      } else {
        console.error("Error starting copy process:", result.data.error);
      }
    } catch (error) {
      console.error("Error in confirm selects:", error);
    }
  };

  const handleReset = () => {
    if (effectiveSelectsMade) return;
    setSelectedImages([]);
    handleCloseDialog();
  };

  const updateImagesWithThumbnails = useCallback((thumbnails, batch, sizeUsed) => {
    if (!thumbnails || !batch || batch.length === 0) return;

    const effectiveSize = sizeUsed ?? desiredThumbnailSizeRef.current ?? null;
    const batchPaths = new Set(batch.map((img) => img.path_lower));

    const shouldUpdateImage = (image) => {
      if (!image || !batchPaths.has(image.path_lower)) return false;
      const newThumbnail = thumbnails[image.path_lower];
      if (!newThumbnail) return false;
      if (!image.thumbnailUrl) return true;
      if (!effectiveSize) return false;
      return image.thumbnailSize !== effectiveSize;
    };

    const applyUpdate = (list) => {
      let changed = false;
      const next = list.map((img) => {
        if (!shouldUpdateImage(img)) {
          return img;
        }
        changed = true;
        const nextSize = effectiveSize ?? img.thumbnailSize ?? null;
        return {
          ...img,
          thumbnailUrl: thumbnails[img.path_lower],
          thumbnailSize: nextSize,
        };
      });
      return changed ? next : list;
    };

    setImages((prev) => applyUpdate(prev));
    setSelectedImages((prev) => applyUpdate(prev));
    setLockedSelectedImages((prev) => applyUpdate(prev));
  }, []);

  const updateImagesWithDirectUrls = useCallback((directUrls, batch) => {
    console.log(`🔄 updateImagesWithDirectUrls called with:`, {
      directUrls,
      batchLength: batch.length,
      batchFiles: batch.map((img) => img.name),
    });

    setImages((prev) => {
      const hasChanges = batch.some(
        (image) =>
          !prev.find((img) => img.path_lower === image.path_lower)?.directUrl &&
          directUrls[image.path_lower]
      );

      console.log(`📊 Images update - hasChanges: ${hasChanges}`);

      return hasChanges
        ? prev.map((img) => {
            const shouldUpdate =
              !img.directUrl &&
              batch.some((batchImg) => batchImg.path_lower === img.path_lower);
            const newDirectUrl = shouldUpdate
              ? directUrls[img.path_lower] || null
              : img.directUrl;

            if (shouldUpdate && newDirectUrl) {
              console.log(
                `✅ Setting directUrl for ${img.name}: ${newDirectUrl}`
              );
            }

            return {
              ...img,
              directUrl: newDirectUrl,
            };
          })
        : prev;
    });

    setSelectedImages((prev) => {
      const selectedImagesHasChanges = batch.some((image) => {
        const selectedImage = prev.find(
          (img) => img.path_lower === image.path_lower
        );
        return (
          selectedImage &&
          !selectedImage.directUrl &&
          directUrls[image.path_lower]
        );
      });

      console.log(
        `📊 SelectedImages update - hasChanges: ${selectedImagesHasChanges}`
      );

      if (!selectedImagesHasChanges) return prev;

      return prev.map((img) => {
        const shouldUpdate =
          !img.directUrl &&
          batch.some((batchImg) => batchImg.path_lower === img.path_lower);
        const newDirectUrl = shouldUpdate
          ? directUrls[img.path_lower] || null
          : img.directUrl;

        if (shouldUpdate && newDirectUrl) {
          console.log(
            `✅ Setting directUrl for selected image ${img.name}: ${newDirectUrl}`
          );
        }

        return {
          ...img,
          directUrl: newDirectUrl,
        };
      });
    });

    setLockedSelectedImages((prev) => {
      const lockedImagesHasChanges = batch.some((image) => {
        const lockedImage = prev.find(
          (img) => img.path_lower === image.path_lower
        );
        return (
          lockedImage && !lockedImage.directUrl && directUrls[image.path_lower]
        );
      });

      console.log(
        `📊 LockedSelectedImages update - hasChanges: ${lockedImagesHasChanges}`
      );

      if (!lockedImagesHasChanges) return prev;

      return prev.map((img) => {
        const shouldUpdate =
          !img.directUrl &&
          batch.some((batchImg) => batchImg.path_lower === img.path_lower);
        const newDirectUrl = shouldUpdate
          ? directUrls[img.path_lower] || null
          : img.directUrl;

        if (shouldUpdate && newDirectUrl) {
          console.log(
            `✅ Setting directUrl for locked selected image ${img.name}: ${newDirectUrl}`
          );
        }

        return {
          ...img,
          directUrl: newDirectUrl,
        };
      });
    });
  }, []);

  // const processThumbnailQueue = useCallback(async () => {
  //   if (thumbnailQueue.current.length === 0) return;

  //   let queue = [...thumbnailQueue.current];

  //   while (queue.length > 0) {
  //     const batch = queue.slice(0, 36); // small batch
  //     try {
  //       const batchThumbnails = await getThumbnails(batch, {
  //         batchSize: 16,
  //         delayBetweenBatches: 600,
  //         maxRetries: 2,
  //         size: "w1024h768",
  //         quality: "quality_80",
  //         mode: "strict",
  //       });
  //       updateImagesWithThumbnails(batchThumbnails, batch);
  //     } catch (error) {
  //       console.error("Error loading batch thumbnails:", error);
  //     }
  //     queue = queue.slice(36);
  //   }

  //   // clear the queue after processing
  //   thumbnailQueue.current = [];
  // }, [updateImagesWithThumbnails]);

  const processThumbnailQueue = useCallback(async () => {
    if (thumbnailQueue.current.length === 0 || isProcessingThumbnailQueue.current)
      return;

    isProcessingThumbnailQueue.current = true;

    try {
      while (thumbnailQueue.current.length > 0) {
        const batch = thumbnailQueue.current.splice(0, 25);
        const sizeForBatch = desiredThumbnailSizeRef.current;

        try {
          const thumbnails = await getThumbnails(batch, {
            size: sizeForBatch,
            quality: "quality_80",
            mode: "strict",
          });
          updateImagesWithThumbnails(thumbnails, batch, sizeForBatch);
        } catch (error) {
          console.error("Error loading thumbnails:", error);
        } finally {
          batch.forEach((img) => {
            if (img?.path_lower) {
              pendingThumbnailPaths.current.delete(img.path_lower);
            }
          });
        }
      }
    } finally {
      isProcessingThumbnailQueue.current = false;
      if (thumbnailQueue.current.length > 0) {
        processThumbnailQueue();
      }
    }
  }, [updateImagesWithThumbnails]);

  const enqueueThumbnails = useCallback(
    (imagesToAdd, { prioritize = false } = {}) => {
      if (!imagesToAdd || imagesToAdd.length === 0) return;

      const deduped = imagesToAdd.filter(
        (img) =>
          img?.path_lower && !pendingThumbnailPaths.current.has(img.path_lower)
      );

      if (deduped.length === 0) return;

      deduped.forEach((img) => pendingThumbnailPaths.current.add(img.path_lower));

      if (prioritize) {
        thumbnailQueue.current.unshift(...deduped);
      } else {
        thumbnailQueue.current.push(...deduped);
      }

      processThumbnailQueue();
    },
    [processThumbnailQueue]
  );

  useEffect(() => {
    if (lastRequestedThumbnailSizeRef.current === desiredThumbnailSize) {
      return;
    }

    const imagesByPath = new Map();
    const collectImages = (list = []) => {
      list.forEach((img) => {
        if (
          !img?.path_lower ||
          imagesByPath.has(img.path_lower) ||
          !img.thumbnailUrl
        ) {
          return;
        }
        if (img.thumbnailSize === desiredThumbnailSize) {
          return;
        }
        imagesByPath.set(img.path_lower, img);
      });
    };

    collectImages(images);
    collectImages(selectedImages);
    collectImages(lockedSelectedImages);

    const toRefresh = Array.from(imagesByPath.values());
    if (toRefresh.length > 0) {
      enqueueThumbnails(toRefresh, { prioritize: true });
    }

    lastRequestedThumbnailSizeRef.current = desiredThumbnailSize;
  }, [
    desiredThumbnailSize,
    images,
    selectedImages,
    lockedSelectedImages,
    enqueueThumbnails,
  ]);

  const loadDirectImagesProgressively = useCallback(
    async (images) => {
      console.log(
        `🎯 Starting direct image loading for ${images.length} images (one at a time to respect API limits)`
      );
      setDirectUrlLoadingInProgress(true);

      try {
        for (let i = 0; i < images.length; i++) {
          const image = images[i];

          try {
            console.log(
              `🔗 Loading direct URL ${i + 1}/${images.length}: ${image.name}`
            );

            // Process one image at a time to let getFullResolutionUrls handle rate limiting
            const directUrls = await getFullResolutionUrls([image]);

            console.log(
              `🔍 Generated URL for ${image.name}:`,
              directUrls[image.path_lower]
            );

            // Update the single image with its direct URL immediately
            updateImagesWithDirectUrls(directUrls, [image]);

            console.log(`✅ Direct URL ${i + 1} loaded successfully`);

            // Small delay before next image (but let getFullResolutionUrls control the main timing)
            if (i < images.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(
              `❌ Error loading direct URL ${i + 1} (${image.name}):`,
              error
            );
            // Continue with next image rather than stopping the entire process
          }
        }
      } finally {
        setDirectUrlLoadingInProgress(false);
        console.log(
          `🎉 Direct URL loading complete for ${images.length} images!`
        );
      }
    },
    [updateImagesWithDirectUrls]
  );

  const loadStoryboardThumbnailsProgressively = useCallback(
    async (images) => {
      console.log(
        `🎯 Starting priority storyboard loading for ${images.length} images with 5-image batches`
      );

      for (let i = 0; i < images.length; i += 5) {
        const batch = images.slice(i, i + 5);

        try {
          console.log(
            `📸 Loading storyboard batch ${Math.floor(i / 5) + 1}/${Math.ceil(
              images.length / 5
            )} (${batch.length} images)`
          );

          const thumbnails = await getThumbnails(batch, {
            batchSize: 5,
            delayBetweenBatches: 100, // Fast internal batching
            maxRetries: 2,
            size: desiredThumbnailSizeRef.current,
            quality: "quality_80",
            mode: "strict",
          });

          // Update the images with their thumbnails immediately
          updateImagesWithThumbnails(
            thumbnails,
            batch,
            desiredThumbnailSizeRef.current
          );

          console.log(`✅ Storyboard batch ${Math.floor(i / 5) + 1} loaded`);

          // Short delay before next batch (unless it's the last batch)
          if (i + 5 < images.length) {
            await new Promise((resolve) => setTimeout(resolve, 150));
          }
        } catch (error) {
          console.error(
            `❌ Error loading storyboard batch ${Math.floor(i / 5) + 1}:`,
            error
          );
        }
      }

      console.log(
        `🎉 All ${images.length} storyboard thumbnails loaded progressively!`
      );
    },
    [updateImagesWithThumbnails]
  );

  const handleAccordionExpand = useCallback(
    (images) => {
      if (useThumbnails) {
        // Only load thumbnails for images that don't have them yet
        const imagesNeedingThumbnails = images.filter(
          (img) =>
            !img.thumbnailUrl || img.thumbnailSize !== desiredThumbnailSize
        );
        if (imagesNeedingThumbnails.length > 0) {
          enqueueThumbnails(imagesNeedingThumbnails, { prioritize: true });
        }
      } else {
        // Load direct URLs for images that don't have them yet
        const imagesNeedingDirectUrls = images.filter((img) => !img.directUrl);
        if (imagesNeedingDirectUrls.length > 0) {
          // Use direct loading with similar batch processing
          loadDirectImagesProgressively(imagesNeedingDirectUrls);
        }
      }
    },
    [
      enqueueThumbnails,
      loadDirectImagesProgressively,
      useThumbnails,
      desiredThumbnailSize,
    ]
  );

  const loadImagesFromPath = async (path, existingCursor = null) => {
    let allEntries = [];
    let currentCursor = existingCursor;
    let hasMoreItems = true;

    while (hasMoreItems) {
      try {
        const result = await listFolderContents(path, currentCursor);
        const entries = result.entries.map((entry) => ({
          ...entry,
          thumbnailUrl: null,
          thumbnailSize: null,
          directUrl: null,
        }));

        allEntries = [...allEntries, ...entries];
        hasMoreItems = result.hasMore;
        currentCursor = result.cursor;

        if (!hasMoreItems) break;
      } catch (error) {
        console.error(`Error loading images from ${path}:`, error);
        hasMoreItems = false;
      }
    }

    return {
      entries: allEntries,
      hasMore: hasMoreItems,
      cursor: currentCursor,
    };
  };

  const loadImages = useCallback(
    async (dropboxPath, selectsAreLocked2, hasDatabaseSelections = false) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setLoading(true);

      try {
        console.log("🚀 Starting fast loading strategy - folders first!");

        // Step 1: IMMEDIATE - Scan folder structure and show it right away
        console.log("📁 Step 1: Scanning folder structure (FAST)");
        let quickProofsResult;

        try {
          quickProofsResult = await scanFolderStructure(
            `${dropboxPath}/Quickproofs`
          );
          if (quickProofsResult.entries.length === 0) {
            quickProofsResult = await scanFolderStructure(
              `${dropboxPath}/Storyboard Selects`
            );
            setRootFolderTitle("Storyboard Selects");
          }
        } catch (error) {
          console.error("Error scanning folder structure:", error);
          quickProofsResult = { entries: [], hasMore: false, cursor: null };
        }

        // IMMEDIATE: Set folder structure (users see folders right away!)
        // Deduplicate by filename to handle cases where same filename exists in multiple subdirectories
        const structureImages = deduplicateByFilename(
          quickProofsResult.entries.sort(sortImagesByFilename)
        );
        setImages(structureImages);
        setLoading(false); // Stop loading spinner - users can see folders now
        isLoadingRef.current = false;

        console.log(
          `📊 Folder structure ready: ${structureImages.length} images found`
        );
        console.log(
          "✅ FOLDERS VISIBLE - User can now see structure immediately!"
        );

        // Step 2: BACKGROUND - Load Storyboard Selects (non-blocking)
        console.log("⭐ Step 2: Loading Storyboard Selects in background...");

        // Don't await this - let it run in background with progressive loading
        (async () => {
          try {
            const selectsResult = await loadImagesFromPath(
              `${dropboxPath}/Storyboard Selects`
            );

            if (selectsResult.entries.length > 0) {
              console.log(
                `📋 Found ${selectsResult.entries.length} storyboard selects - setting up progressive loading`
              );

              // Create filename lookup for matching structure images to selects
              // Reverse the array so that when Map overwrites duplicates, the FIRST occurrence wins
              const selectsFilenames = new Map(
                selectsResult.entries
                  .sort(sortImagesByFilename)
                  .reverse()
                  .map((entry) => [entry.path_display.split("/").pop(), entry])
              );

              // IMMEDIATE: Pre-mark selected images without thumbnails (users see selections right away)
              // Track matched filenames to ensure each is only matched once
              const matchedFilenames = new Set();
              const selectedFromStructure = structureImages
                .filter((image) => {
                  const filename = image.path_display.split("/").pop();
                  if (selectsFilenames.has(filename) && !matchedFilenames.has(filename)) {
                    matchedFilenames.add(filename);
                    return true;
                  }
                  return false;
                })
                .map((image) => ({
                  ...image,
                  storyboardPath: selectsFilenames.get(
                    image.path_display.split("/").pop()
                  ).path_lower,
                  thumbnailUrl: null, // No thumbnail yet - will load progressively
                  thumbnailSize: null,
                  directUrl: null, // No direct URL yet - will load progressively if needed
                }));

              // Use the working pattern from older versions - only set if no existing selections
              if (!hasDatabaseSelections) {
                if (selectsAreLocked2) {
                  setLockedSelectedImages((prev) => {
                    if (prev.length > 0) return prev; // Don't override existing selections
                    return selectedFromStructure.sort(sortImagesByFilename);
                  });
                } else {
                  setSelectedImages((prev) => {
                    if (prev.length > 0) return prev; // Don't override existing selections
                    return selectedFromStructure.sort(sortImagesByFilename);
                  });
                }
                console.log(
                  "✅ Storyboard selections set (no database selections found)"
                );
              } else {
                console.log(
                  "🔒 Skipping storyboard selections - database selections take priority"
                );
              }

              console.log(
                "✅ Storyboard selections visible immediately (thumbnails loading progressively...)"
              );

              // PROGRESSIVE: Use appropriate loading method based on useThumbnails setting
              if (useThumbnails) {
                loadStoryboardThumbnailsProgressively(selectedFromStructure);
              } else {
                loadDirectImagesProgressively(selectedFromStructure);
              }

              setPriorityImagesLoaded(true);
            }
          } catch (error) {
            console.error("Error loading storyboard selects:", error);
          }
        })();
      } catch (error) {
        console.error("Error loading images:", error);
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [
      priorityImagesLoaded,
      processThumbnailQueue,
      loadStoryboardThumbnailsProgressively,
      loadDirectImagesProgressively,
      useThumbnails,
      directUrlLoadingInProgress,
    ]
  );

  useEffect(() => {
    let unsubscribe;

    const main = async () => {
      const jobDataRaw = await getDoc(doc(db, "JobData", jobID));
      const jobDocLoc = jobDataRaw.data();
      setJobDoc(jobDocLoc);
      const jn = `${jobDocLoc.jobNumber}-${jobDocLoc.client}${jobDocLoc.jobTypeShort}`;
      setJobName(jn);
      let dbPath = `/${jobDocLoc.client}/${jn}`;
      if (jobDocLoc.multipleDays) {
        dbPath = `/${jobDocLoc.client}/${jn}/${jobDocLoc.multipleDays}`;
        // console.log(jobDocLoc.multipleDays);
      }
      setDropboxPath(dbPath);

      const clientSelectsRaw = await getDoc(doc(db, "ClientSelects", jobID));
      const clientSelectsDoc = clientSelectsRaw.data();

      if (!clientSelectsRaw.exists) {
        return;
      }

      if (clientSelectsDoc?.selectsMade) {
        setSelectsMade(true);
        setSelectsMadeDialogOpen(true);
      }
      if (jobDocLoc?.selectsAreLocked) {
        setSelectsAreLocked(true);
      }

      // Restore database selections BEFORE storyboard loading to ensure persistence
      let hasDatabaseSelections = false;
      if (clientSelectsDoc.selectedImages !== undefined) {
        if (clientSelectsDoc.selectedImages.length > 0) {
          hasDatabaseSelections = true;
          const updatedSelectedImages = clientSelectsDoc.selectedImages.map(
            (selectedImage) => {
              const selectedFileName = selectedImage.path_display
                .split("/")
                .pop();
              const matchingImage = images.find(
                (img) => img.path_display.split("/").pop() === selectedFileName
              );
              return {
                ...selectedImage,
                thumbnailUrl:
                  matchingImage?.thumbnailUrl ??
                  selectedImage.thumbnailUrl ??
                  null,
                thumbnailSize:
                  matchingImage?.thumbnailSize ??
                  selectedImage.thumbnailSize ??
                  null,
              };
            }
          );
          enqueueThumbnails(updatedSelectedImages, { prioritize: true });

          // Respect selectsAreLocked state when loading from database
          if (jobDocLoc?.selectsAreLocked) {
            setLockedSelectedImages(updatedSelectedImages);
          } else {
            setSelectedImages(updatedSelectedImages);
          }
        } else {
          // Handle empty array (reset case) - don't count as having database selections
          // This allows storyboard selections to preload when database is empty
          hasDatabaseSelections = false;
          if (jobDocLoc?.selectsAreLocked) {
            setLockedSelectedImages([]);
          } else {
            setSelectedImages([]);
          }
        }
      }

      loadImages(dbPath, jobDocLoc?.selectsAreLocked, hasDatabaseSelections);
    };
    main();
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const handleImageSelect = useCallback(
    (imageOrImages, isBulkRemove = false) => {
      if (effectiveSelectsMade) return;
      setSelectedImages((prev) => {
        if (isBulkRemove) {
          const imagesToRemove = Array.isArray(imageOrImages)
            ? imageOrImages
            : [imageOrImages];
          // Track the first removed image for navigation
          if (imagesToRemove.length > 0) {
            setLastChangedImage(imagesToRemove[0]);
          }
          return prev
            .filter(
              (img) =>
                !imagesToRemove.some((toRemove) => toRemove.name === img.name)
            )
            .sort(sortImagesByFilename);
        }

        const image = Array.isArray(imageOrImages)
          ? imageOrImages[0]
          : imageOrImages;
        const isSelected = prev.some((img) => img.name === image.name);

        // Track the changed image for navigation
        setLastChangedImage(image);

        if (isSelected) {
          const resDS = prev
            .filter((img) => img.name !== image.name)
            .sort(sortImagesByFilename);
          updateDoc(doc(db, "ClientSelects", jobID), {
            selectedImages: clearThumbnails(resDS),
          });

          return resDS;
        }

        const res = [...prev, image].sort(sortImagesByFilename);
        updateDoc(doc(db, "ClientSelects", jobID), {
          selectedImages: clearThumbnails(res),
        });
        return res;
      });
    },
    [effectiveSelectsMade, jobID]
  );

  // Handle image removal from context menu
  const handleRemoveImage = useCallback(
    (imageToRemove) => {
      if (effectiveSelectsMade) {
        console.log("Cannot remove image - selects are locked");
        return;
      }

      console.log("Removing image from ImageGrid:", imageToRemove.name);

      // Track the removed image for navigation
      setLastChangedImage(imageToRemove);

      setSelectedImages((prev) => {
        const filteredImages = prev.filter(
          (img) => img.name !== imageToRemove.name
        );

        // Update database
        updateDoc(doc(db, "ClientSelects", jobID), {
          selectedImages: clearThumbnails(filteredImages),
        })
          .then(() => {
            console.log("Image removed from database");
          })
          .catch((error) => {
            console.error("Error removing image from database:", error);
          });

        return filteredImages.sort(sortImagesByFilename);
      });
    },
    [effectiveSelectsMade, jobID]
  );

  const itemSize = Math.floor((dimensions.width - 96) / gridColumns);

  const [folderStructure, setFolderStructure] = useState({});
  console.log(folderStructure);
  useEffect(() => {
    const fs = organizeImagesIntoFolders(images, `${dropboxPath}/Quickproofs`);

    setFolderStructure(fs);
  }, [images, dropboxPath]);

  return (
    <>
      {!embedded && (
        <AppBar
          position="fixed"
          sx={{
            background: (theme) =>
              theme.palette.mode === "light"
                ? "#ffffff"
                : theme.palette.background.paper,
            backdropFilter: "blur(8px)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            borderBottom: (theme) =>
              `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"}`,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            padding: "0 !important",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", py: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                minWidth: 300,
                alignItems: "center",
              }}
            >
              <img
                src="https://www.imagesthatsell.com.au/ITS_LOGO_SMALL.png"
                alt="ITS Logo"
                style={{
                  height: 50,
                  filter: theme.palette.mode === "dark" ? "invert(0.85)" : "",
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {getJobName(jobDoc)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {formatJobDate(jobDoc.start)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, minWidth: 500, alignItems: "center" }}>
              <Select
                value={gridColumns}
                onChange={(e) => setGridColumns(e.target.value)}
                sx={{
                  height: 40,
                  width: 130,
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) => theme.palette.mode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) => theme.palette.mode === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <MenuItem value={1}>1 Across</MenuItem>
                <MenuItem value={2}>2 Across</MenuItem>
                <MenuItem value={3}>3 Across</MenuItem>
                <MenuItem value={4}>4 Across</MenuItem>
                <MenuItem value={5}>5 Across</MenuItem>
              </Select>
              {!effectiveSelectsMade ? (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpenDialog}
                  sx={{
                    minWidth: 180,
                    borderRadius: "4px",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                  disabled={effectiveSelectsMade}
                >
                  Reset Selects
                </Button>
              ) : (
                <div style={{ minWidth: 180 }}></div>
              )}
              <Button
                variant="contained"
                color="primary"
                sx={{
                  minWidth: 240,
                  borderRadius: "4px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
                disabled={effectiveSelectsMade}
                onClick={() => setConfirmSelectsDialogOpen(true)}
              >
                {effectiveSelectsMade
                  ? `Selects Confirmed (${selectedImages.length})`
                  : `Confirm Selects (${selectedImages.length})`}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <ResizableLayout embedded={embedded}>
        <div
          style={{
            height: embedded ? "100%" : `calc(${height}px - 70px)`,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            marginTop: embedded ? "0px" : "64px",
          }}
        >
          {!effectiveSelectsMade && (
            <InstructionsDialog
              open={instructionsOpen}
              onClose={() => setInstructionsOpen(false)}
            />
          )}
          {effectiveSelectsMade && (
            <SelectsMadeDialog
              open={selectsMadeDialogOpen}
              onClose={() => setSelectsMadeDialogOpen(false)}
            />
          )}
          <ResetSelectsDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            onReset={handleReset}
          />
          <ConfirmSelectsDialog
            open={confirmSelectsDialogOpen}
            onClose={() => setConfirmSelectsDialogOpen(false)}
            onConfirm={handleConfirmSelects}
            selectedCount={selectedImages.length}
          />

          <div ref={containerRef} style={{ flex: 1, padding: "8px" }}>
            {dimensions.width > 0 && (
              <>
                {folderStructure.noFolder && (
                  <EnhancedAccordion
                    title={rootFolderTitle}
                    images={folderStructure.noFolder}
                    selectedImages={selectedImages}
                    lockedSelectedImages={lockedSelectedImages}
                    onImageSelect={handleImageSelect}
                    gridColumns={gridColumns}
                    dimensions={dimensions}
                    itemSize={itemSize}
                    onExpand={handleAccordionExpand}
                    selectsMade={effectiveSelectsMade}
                  />
                )}

                {/* For other folders */}
                {folderStructure &&
                  typeof folderStructure === "object" &&
                  Object.entries(folderStructure)
                    .filter(([key]) => key !== "noFolder")
                    .map(([folderName, content]) => (
                      <EnhancedAccordion
                        key={folderName}
                        title={folderName}
                        images={content.files || []}
                        selectedImages={selectedImages}
                        lockedSelectedImages={lockedSelectedImages}
                        onImageSelect={handleImageSelect}
                        gridColumns={gridColumns}
                        dimensions={dimensions}
                        itemSize={itemSize}
                        onExpand={handleAccordionExpand}
                        subfolders={content.subfolders || {}}
                        selectsMade={effectiveSelectsMade}
                      />
                    ))}
              </>
            )}
            {loading && (
              <AccordionSkeleton
                count={20}
                gridColumns={gridColumns}
                className="p-2"
              />
            )}
          </div>
        </div>
        <SelectedImagesGrid
          selectedImages={selectedImages}
          lockedSelectedImages={lockedSelectedImages}
          jobName={jobName}
          embedded={embedded}
          onRemoveImage={handleRemoveImage}
          lastChangedImage={lastChangedImage}
        />
      </ResizableLayout>
    </>
  );
};

export default ImageGrid;
